<script setup>
/**
 * OCHAT: la guía de uso, dentro de la propia aplicación.
 *
 * Reemplaza los enlaces "Aprender más" que apuntaban a la documentación de
 * Chatwoot. El contenido está en ./topics.js; acá solo se lo renderiza.
 *
 * No usa SettingsLayout a propósito: esa pieza vive en
 * routes/dashboard/settings/ y trae lógica de listados y estados de carga que no
 * hace falta. Las clases son las del design system de upstream, y como
 * app/javascript/ochat/** está en el `content` de Tailwind, no se purgan.
 */
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { getGuideTopic, guideTopicList } from './topics';

const route = useRoute();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const topics = guideTopicList();
const activeKey = computed(() => route.params.topic);
const topic = computed(() => getGuideTopic(activeKey.value));

const topicRoute = key => accountScopedRoute('ochat_guide', { topic: key });
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-y-auto bg-n-background">
    <div class="flex flex-col w-full max-w-4xl gap-8 px-6 py-8 mx-auto">
      <header class="flex flex-col gap-2">
        <p class="mb-0 text-sm font-medium text-n-slate-11">
          {{ t('OCHAT_GUIDE.EYEBROW') }}
        </p>
        <h1 class="text-heading-1 text-n-slate-12">
          {{ topic ? topic.title : t('OCHAT_GUIDE.NOT_FOUND_TITLE') }}
        </h1>
        <p v-if="topic" class="mb-0 max-w-3xl text-body-main text-n-slate-11">
          {{ topic.summary }}
        </p>
        <p v-else class="mb-0 max-w-3xl text-body-main text-n-slate-11">
          {{ t('OCHAT_GUIDE.NOT_FOUND_BODY') }}
        </p>
      </header>

      <!-- El índice va arriba y siempre visible: desde el enlace de una pantalla
           se entra a un tema puntual, y desde ahí conviene poder saltar al resto
           sin volver a la configuración. -->
      <nav
        class="flex flex-wrap gap-2 pb-6 border-b border-n-weak"
        :aria-label="t('OCHAT_GUIDE.INDEX')"
      >
        <router-link
          v-for="item in topics"
          :key="item.key"
          :to="topicRoute(item.key)"
          class="px-3 py-1.5 text-sm font-medium rounded-lg"
          :class="
            item.key === activeKey
              ? 'bg-n-slate-3 text-n-slate-12'
              : 'text-n-slate-11 hover:bg-n-slate-2 hover:text-n-slate-12'
          "
        >
          {{ item.title }}
        </router-link>
      </nav>

      <article v-if="topic" class="flex flex-col gap-8">
        <section
          v-for="section in topic.sections"
          :key="section.heading"
          class="flex flex-col gap-3"
        >
          <h2 class="text-heading-2 text-n-slate-12">
            {{ section.heading }}
          </h2>

          <p
            v-if="section.body"
            class="mb-0 max-w-3xl text-body-main text-n-slate-11"
          >
            {{ section.body }}
          </p>

          <ol
            v-if="section.steps"
            class="flex flex-col gap-2 mb-0 ltr:pl-5 rtl:pr-5 list-decimal max-w-3xl text-body-main text-n-slate-11"
          >
            <li v-for="(step, index) in section.steps" :key="index">
              {{ step }}
            </li>
          </ol>

          <dl v-if="section.items" class="flex flex-col gap-4 mb-0 max-w-3xl">
            <div
              v-for="item in section.items"
              :key="item.term"
              class="flex flex-col gap-1"
            >
              <dt class="text-body-main font-medium text-n-slate-12">
                {{ item.term }}
              </dt>
              <dd class="mb-0 text-body-main text-n-slate-11">
                {{ item.description }}
              </dd>
            </div>
          </dl>
        </section>
      </article>
    </div>
  </div>
</template>
